import React, { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  useMaterialReactTable,
} from "material-react-table";
import { getArbMonitorColumns } from "./table/columnsConfig/arbsMonitor";
import { PageHeader } from "./ui";

const ArbMonitor = ({ arb_monitor_data }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // ===== STATE =====
  const [savedGlobalFilter, setSavedGlobalFilter] = useState(
    localStorage.getItem("arbMonitorTableGlobalFilter") || "",
  );
  const [globalFilter, setGlobalFilter] = useState(savedGlobalFilter);

  // ===== EFFECTS =====
  useEffect(() => {
    if (globalFilter !== savedGlobalFilter) {
      setSavedGlobalFilter(globalFilter || "");
      if (!globalFilter) {
        localStorage.removeItem("arbMonitorTableGlobalFilter");
      } else {
        localStorage.setItem("arbMonitorTableGlobalFilter", globalFilter);
      }
    }
  }, [globalFilter, savedGlobalFilter]);

  // ===== CONFIG =====
  const tableConfig = {
    columns: getArbMonitorColumns(),
    data: arb_monitor_data || [],
    enableColumnDragging: false,
    enableSorting: true,
    enableColumnOrdering: false,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    columnResizeMode: "onChange",
    enableTopToolbar: false,
    enableColumnActions: false,
    enableRowVirtualization: false,
    enableDensityToggle: false,
    virtualizerProps: { overscan: 10 },
    defaultDisplayColumn: { enableResizing: false },
    muiTableHeadCellProps: {
      sx: {
        fontSize: "0.8rem",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        padding: theme.spacing(1),
        fontSize: "0.75rem",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
    muiTableBodyRowProps: {
      sx: {
        fontSize: "0.75rem",
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: "25px",
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "calc(100vh - 200px)", // Adjusted for mobile
          sm: "calc(100vh - 150px)", // Default for desktop
        },
        width: "100%", // Full width for responsiveness
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        minWidth: "100%",
        backgroundColor: theme.palette.background.default,
        "&::-webkit-scrollbar": {
          height: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableFooterRowProps: {
      sx: {
        backgroundColor: theme.palette.primary.dark,
        fontWeight: 600,
        position: "sticky",
        bottom: 0,
        zIndex: 2,
        borderTop: `2px solid ${theme.palette.divider}`,
      },
    },
    muiTableProps: {
      sx: {
        minWidth: "1400px",
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: "35px",
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: theme.spacing(0.75, 0.5, 0.25, 0.5),
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: theme.spacing(1.5, 1),
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: "0.875rem",
          color: theme.palette.primary.contrastText,
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
      },
    },
    initialState: {
      density: "compact",
      sorting: [{ id: "Target", desc: false }],
      showColumnFilters: false,
      showGlobalFilter: true,
      globalFilter: savedGlobalFilter,
      columnPinning: {
        left: ["Target", "Acquiror"],
      },
    },
    state: {
      globalFilter: globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
  };

  // ===== MEMOIZED VALUES =====
  const arb_monitor_table = useMaterialReactTable(tableConfig);

  // ===== HANDLERS =====
  // (No custom handlers in this component)

  // ===== RENDER LOGIC =====
  if (!arb_monitor_data || arb_monitor_data?.length === 0) {
    return (
      <Box
        sx={{
          height: "calc(100vh - 150px)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography>Loading data...</Typography>
      </Box>
    );
  }

  // ===== RETURN =====
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
        borderRadius: 0,
        boxShadow: theme.shadows[1],
      }}
    >
      <PageHeader
        sx={{ px: "20px", justifyContent: "flex-start", alignItems: "end" }}
        title={"Arb Monitor"}
        action={
          <Box
            sx={{
              display: "flex",
              alignItems: "center", // vertically center items
              justifyContent: "flex-start", // left align
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {/* Search Field */}
            <Box sx={{ maxWidth: "280px", flexShrink: 0 }}>
              <MRT_GlobalFilterTextField
                table={arb_monitor_table}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": {
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.divider}`,
                    fontSize: "0.875rem",
                    "&:hover": {
                      backgroundColor: theme.palette.background.paper,
                      borderColor: theme.palette.primary.dark,
                    },
                    "&.Mui-focused": {
                      backgroundColor: theme.palette.background.paper,
                      borderColor: theme.palette.primary.dark,
                      boxShadow: `0 0 0 2px ${theme.palette.primary.dark}20`,
                    },
                  },
                  "& .MuiInputBase-input": {
                    padding: theme.spacing(0.75, 1.25),
                    color: theme.palette.text.primary,
                    "&::placeholder": {
                      color: theme.palette.text.secondary,
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>
          </Box>
        }
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#f5f5f5",
          paddingLeft: "8px",
          height: "calc(100% - 40px)",
          overflow: "hidden",
        }}
      >
        <MaterialReactTable
          table={arb_monitor_table}
          muiTablePaperProps={{
            sx: {
              height: "100%",
              display: "flex",
              flexDirection: "column",
            },
          }}
          muiTableContainerProps={{
            sx: {
              flex: "1 1 auto",
              overflow: "auto",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default ArbMonitor;
